import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bird, Layers, Egg, Activity, Wheat, Warehouse, Percent } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { KpiCard } from '../components/dashboard/KpiCard';
import { RegistrosFaltantesBanner } from '../components/dashboard/RegistrosFaltantesBanner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { getDashboardResumen, getProduccionSemanal, getMortalidadTendencia, getProduccionDiaria, getLotes, getNaves, getPosturaTotal } from '../lib/api';

const CHART_COLORS = {
  produccion: '#006400',
  mortalidad: '#B91C1C',
  grandes: '#006400',
  medianos: '#16A34A',
  pequenos: '#0EA5A4',
  dobles: '#F2C94C',
  sucios: '#94A3B8',
  casquiados: '#64748B',
};

const PIE_COLORS = ['#006400', '#16A34A', '#0EA5A4', '#F2C94C', '#94A3B8', '#64748B'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-sm p-3 text-sm">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {entry.value?.toLocaleString('es-ES')}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [resumen, setResumen] = useState(null);
  const [prodSemanal, setProdSemanal] = useState([]);
  const [mortalidad, setMortalidad] = useState([]);
  const [prodDiaria, setProdDiaria] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [naves, setNaves] = useState([]);
  const [filtroLote, setFiltroLote] = useState('all');
  const [filtroNave, setFiltroNave] = useState('all');
  const [periodoPostura, setPeriodoPostura] = useState('dia');
  const [posturaTotal, setPosturaTotal] = useState(null);
  const [loadingPostura, setLoadingPostura] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadCharts = useCallback(async () => {
    try {
      const params = {};
      if (filtroLote && filtroLote !== 'all') params.lote_id = filtroLote;
      if (filtroNave && filtroNave !== 'all') params.nave_id = filtroNave;

      const [semanalRes, mortRes, diariaRes] = await Promise.all([
        getProduccionSemanal(params),
        getMortalidadTendencia(params),
        getProduccionDiaria(params),
      ]);
      setProdSemanal(semanalRes.data);
      setMortalidad(mortRes.data);
      setProdDiaria(diariaRes.data);
    } catch (err) {
      console.error('Error loading charts:', err);
    }
  }, [filtroLote, filtroNave]);

  const loadPostura = useCallback(async () => {
    try {
      setLoadingPostura(true);
      const params = { periodo: periodoPostura };
      if (filtroLote && filtroLote !== 'all') params.lote_id = filtroLote;
      if (filtroNave && filtroNave !== 'all') params.nave_id = filtroNave;
      const res = await getPosturaTotal(params);
      setPosturaTotal(res.data);
    } catch (err) {
      console.error('Error loading postura total:', err);
      setPosturaTotal(null);
    } finally {
      setLoadingPostura(false);
    }
  }, [filtroLote, filtroNave, periodoPostura]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [resumenRes, lotesRes, navesRes] = await Promise.all([
        getDashboardResumen(),
        getLotes(),
        getNaves(),
      ]);
      setResumen(resumenRes.data);
      setLotes(lotesRes.data);
      setNaves(navesRes.data);
      await loadCharts();
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [loadCharts]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadCharts(); }, [loadCharts]);
  useEffect(() => { loadPostura(); }, [loadPostura]);

  // Prepare pie chart data from latest daily production (memoized)
  const pieData = useMemo(() => (prodDiaria.length > 0 ? [
    { name: 'Grandes', value: prodDiaria[prodDiaria.length - 1]?.grandes || 0 },
    { name: 'Medianos', value: prodDiaria[prodDiaria.length - 1]?.medianos || 0 },
    { name: 'Pequeños', value: prodDiaria[prodDiaria.length - 1]?.pequenos || 0 },
    { name: 'Dobles', value: prodDiaria[prodDiaria.length - 1]?.dobles || 0 },
    { name: 'Sucios', value: prodDiaria[prodDiaria.length - 1]?.sucios || 0 },
    { name: 'Casquiados', value: prodDiaria[prodDiaria.length - 1]?.casquiados || 0 },
  ].filter(d => d.value > 0) : []), [prodDiaria]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Aviso de registros faltantes (solo si hay) */}
      <RegistrosFaltantesBanner />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 lg:gap-4" data-testid="kpi-grid">
        <KpiCard
          title="Gallinas Totales"
          value={resumen?.total_gallinas || 0}
          icon={Bird}
          subtitle="Aves activas"
          testId="kpi-gallinas-totales"
        />
        <KpiCard
          title="Lotes Activos"
          value={resumen?.total_lotes_activos || 0}
          icon={Layers}
          subtitle={`${resumen?.total_naves || 0} naves`}
          testId="kpi-lotes-activos"
        />
        <KpiCard
          title="Stock de Huevos"
          value={resumen?.total_stock_huevos || 0}
          icon={Egg}
          subtitle="En almacén"
          testId="kpi-stock-huevos"
        />
        <KpiCard
          title="Producción Hoy"
          value={resumen?.produccion_hoy || 0}
          icon={Warehouse}
          subtitle={`Mortalidad: ${resumen?.mortalidad_hoy || 0}`}
          testId="kpi-produccion-hoy"
        />

        {/* % Postura Total — KPI con selector de período integrado */}
        <Card
          className="hover:shadow-[0_10px_24px_rgba(15,23,42,0.10)] transition-shadow duration-200 border-primary/30 bg-primary/[0.03]"
          data-testid="kpi-postura-total"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <p className="text-sm font-medium text-muted-foreground">% Postura Total</p>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/15">
              <Percent className="w-[18px] h-[18px] text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div
              className="text-2xl md:text-3xl font-bold tabular-nums text-primary"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="kpi-postura-total-valor"
            >
              {loadingPostura
                ? '…'
                : posturaTotal && posturaTotal.gallinas_activas > 0
                  ? `${posturaTotal.postura_pct.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
                  : '—'}
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className="text-[11px] text-muted-foreground leading-tight truncate">
                {posturaTotal && posturaTotal.gallinas_activas > 0
                  ? `${(posturaTotal.total_huevos || 0).toLocaleString('es-ES')} huevos · ${(posturaTotal.gallinas_activas || 0).toLocaleString('es-ES')} gallinas`
                  : 'Sin datos para el filtro'}
              </p>
              <Select value={periodoPostura} onValueChange={setPeriodoPostura}>
                <SelectTrigger
                  className="h-7 w-[110px] text-xs shrink-0"
                  data-testid="kpi-postura-periodo"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia" data-testid="kpi-postura-periodo-dia">Día</SelectItem>
                  <SelectItem value="semana" data-testid="kpi-postura-periodo-semana">Semana</SelectItem>
                  <SelectItem value="acumulado" data-testid="kpi-postura-periodo-acumulado">Acumulado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {posturaTotal && posturaTotal.fecha_desde && (
              <p className="text-[10px] text-muted-foreground mt-1 leading-tight">
                {posturaTotal.periodo === 'dia'
                  ? `Día: ${new Date(posturaTotal.fecha_hasta + 'T00:00:00').toLocaleDateString('es-ES')}`
                  : `${new Date(posturaTotal.fecha_desde + 'T00:00:00').toLocaleDateString('es-ES')} → ${new Date(posturaTotal.fecha_hasta + 'T00:00:00').toLocaleDateString('es-ES')}`}
                {filtroNave === 'all' && filtroLote === 'all'
                  ? ` · ${posturaTotal.num_naves} nave(s)`
                  : ''}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Filtrar por:</span>
          <Select value={filtroLote} onValueChange={(v) => { setFiltroLote(v); setFiltroNave('all'); }} data-testid="filtro-lote">
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Todos los lotes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los lotes</SelectItem>
              {lotes.map(l => (
                <SelectItem key={l.id || l._id} value={l.id || l._id}>{l.numero_lote}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtroNave} onValueChange={setFiltroNave} data-testid="filtro-nave">
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Todas las naves" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las naves</SelectItem>
              {naves.filter(n => filtroLote === 'all' || n.lote_id === filtroLote).map(n => (
                <SelectItem key={n.id || n._id} value={n.id || n._id}>{n.nombre_nave}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Weekly Production */}
        <Card className="xl:col-span-2" data-testid="chart-produccion-semanal">
          <CardHeader>
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Producción Semanal</CardTitle>
          </CardHeader>
          <CardContent>
            {prodSemanal.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={prodSemanal}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 90%)" opacity={0.6} />
                  <XAxis dataKey="semana" tick={{ fontSize: 12 }} stroke="hsl(215, 16%, 40%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(215, 16%, 40%)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total_huevos" name="Total Huevos" fill={CHART_COLORS.produccion} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                <p>Sin datos de producción aún</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Egg Distribution Pie */}
        <Card data-testid="chart-porcentaje-huevos">
          <CardHeader>
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Distribución de Huevos</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                <p>Sin datos disponibles</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mortality Trend + Daily Production */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card data-testid="chart-tendencia-mortalidad">
          <CardHeader>
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Tendencia de Mortalidad</CardTitle>
          </CardHeader>
          <CardContent>
            {mortalidad.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={mortalidad}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 90%)" opacity={0.6} />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11 }} stroke="hsl(215, 16%, 40%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(215, 16%, 40%)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="mortalidad" name="Mortalidad" stroke={CHART_COLORS.mortalidad} strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-muted-foreground">
                <p>Sin datos de mortalidad aún</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="chart-produccion-diaria">
          <CardHeader>
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Producción Diaria por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {prodDiaria.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={prodDiaria}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 90%)" opacity={0.6} />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11 }} stroke="hsl(215, 16%, 40%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(215, 16%, 40%)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="grandes" name="Grandes" fill={CHART_COLORS.grandes} stackId="a" />
                  <Bar dataKey="medianos" name="Medianos" fill={CHART_COLORS.medianos} stackId="a" />
                  <Bar dataKey="pequenos" name="Pequeños" fill={CHART_COLORS.pequenos} stackId="a" />
                  <Bar dataKey="dobles" name="Dobles" fill={CHART_COLORS.dobles} stackId="a" />
                  <Bar dataKey="sucios" name="Sucios" fill={CHART_COLORS.sucios} stackId="a" />
                  <Bar dataKey="casquiados" name="Casquiados" fill={CHART_COLORS.casquiados} stackId="a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-muted-foreground">
                <p>Sin datos de producción diaria</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
